import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Row, Col, CardBody, Table } from "reactstrap";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

import withRouter from "./withRouter";
import { withTranslation } from "react-i18next";

import GlobalSearchBar from "./TableContainerUtilities/GlobalSearchBar";
import ColumnFilterInput from "./TableContainerUtilities/ColumnFilterInput";
import PaginationControls from "./TableContainerUtilities/PaginationControls";

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize = 10,
  tableClass = "table table-bordered align-middle",
  theadClass,
  trClass,
  tdClass,
  divClass = "table-responsive",
  tablePage,
  tableTotal,
  onNextPage,
  onSearch,
  tableTotalPage,
  SearchPlaceholder,
  showPagination = true,
  footer,
  enableRowSelection = false, // ✅ default OFF
  onSelectionChange, // ✅ optional callback
  t = (key) => key,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // -------------------------
  // Fuzzy filter
  // -------------------------
  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  // -------------------------
  // Optional Selection Column
  // -------------------------
  const selectionColumn = useMemo(() => {
    if (!enableRowSelection) return null;

    return {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 40,
    };
  }, [enableRowSelection]);

  // Merge columns conditionally
  const memoColumns = useMemo(() => {
    if (enableRowSelection && selectionColumn) {
      return [selectionColumn, ...columns];
    }
    return columns;
  }, [columns, selectionColumn, enableRowSelection]);

  const memoData = useMemo(() => data || [], [data]);

  // -------------------------
  // Table Instance
  // -------------------------
  const table = useReactTable({
    columns: memoColumns,
    data: memoData,
    filterFns: { fuzzy: fuzzyFilter },
    state: {
      columnFilters,
      globalFilter,
      rowSelection,
    },
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Set page size
  useEffect(() => {
    table.setPageSize(customPageSize);
  }, [customPageSize]);

  // -------------------------
  // Expose Selected Rows
  // -------------------------
  useEffect(() => {
    if (enableRowSelection && onSelectionChange) {
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);

      onSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  // -------------------------
  // Global Search
  // -------------------------
  const handleGlobalSearch = useCallback(
    (value) => {
      setGlobalFilter(value);

      if (value.length > 2 && onSearch) {
        onSearch(value);
      }

      if (value.length === 0 && onNextPage) {
        onNextPage();
      }
    },
    [onSearch, onNextPage],
  );

  // -------------------------
  // Pagination
  // -------------------------
  const handlePageChange = useCallback(
    (page) => {
      const pageSize = customPageSize || 10;

      if (isGlobalFilter && globalFilter !== "" && onSearch) {
        onSearch(globalFilter, page, pageSize);
      } else if (onNextPage) {
        onNextPage(page, pageSize);
      }

      setCurrentPage(page);
    },
    [onNextPage, onSearch, isGlobalFilter, globalFilter, customPageSize],
  );

  // -------------------------
  // Render
  // -------------------------
  return (
    <Fragment>
      {isGlobalFilter && (
        <Row className="mb-2 show-print">
          <CardBody>
            <Row>
              <Col sm={5}>
                <GlobalSearchBar
                  value={globalFilter}
                  onSearch={handleGlobalSearch}
                  placeholder={SearchPlaceholder}
                />
              </Col>
            </Row>
          </CardBody>
        </Row>
      )}

      <div className={divClass}>
        <Table className={tableClass}>
          <thead className={theadClass}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={header.column.columnDef?.style || "text-start"}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ cursor: "pointer" }}>
                    {header.isPlaceholder ? null : (
                      <>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}

                        {{
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted()] ?? null}

                        {header.column.getCanFilter() && (
                          <ColumnFilterInput column={header.column} />
                        )}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length > 0 ?
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td className={tdClass} key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            : <tr>
                <td colSpan={memoColumns.length}>
                  <div className="d-flex justify-content-center p-4">
                    <div className="text-center">
                      <i className="fa-solid fa-triangle-exclamation global_empty"></i>
                      <h6>{t("no_result_found")}</h6>
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>

          {footer && <tfoot>{footer}</tfoot>}
        </Table>
      </div>

      {showPagination && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={tableTotalPage}
          onPageChange={handlePageChange}
          tablePage={tablePage}
          tableTotalPage={tableTotalPage}
          tableTotal={tableTotal}
          t={t}
        />
      )}
    </Fragment>
  );
};

export default withRouter(withTranslation()(TableContainer));
